"use client";

import React, { useCallback, useEffect, useRef, useState } from "react";
import Image from "next/image";

import kinervaLogo from "../Logo/kinerva.svg";
const styles = new Proxy({"Scrim":"Scrim-0f3b2","Modal":"Modal-0f3b2","Close":"Close-0f3b2","Body":"Body-0f3b2"}, { get: (m, k) => (typeof k === "string" ? (m[k] ?? k) : m[k]) });
import RichText from "payload-pms/RichText";

export type PopupProps = {
  block: {
    enabled?: boolean | null;
    delay?: number | null;
    content: any;
    anchor?: string | null;
    redirectTimeout?: number | null;
    redirectUrl?: string | null;
  };
};

// The popup is rendered inside the admin live-preview iframe under this path.
// In preview we always show the popup and never redirect the window.
const isLivePreview = () =>
  typeof window !== "undefined" &&
  window.location.pathname.startsWith("/admin/preview");

const Popup = ({ block }: PopupProps) => {
  const { enabled, delay, content, anchor, redirectTimeout, redirectUrl } =
    block;

  const [open, setOpen] = useState(false);
  const modalRef = useRef<HTMLDivElement>(null);

  const storageKey = `kinerva-popup-dismissed:${anchor ?? ""}`;

  const close = useCallback(() => {
    setOpen(false);
    try {
      window.sessionStorage.setItem(storageKey, "1");
    } catch {
      // sessionStorage unavailable
    }
  }, [storageKey]);

  // Any link within the popup pointing at "#close" dismisses it.
  const onModalClick = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      event.stopPropagation();
      const anchorEl = (event.target as HTMLElement).closest("a");
      if (anchorEl?.getAttribute("href")?.endsWith("#close")) {
        event.preventDefault();
        close();
      }
    },
    [close],
  );

  useEffect(() => {
    // In live preview the "Enabled" switch drives show/hide, and when shown we
    // display it right away, ignoring any prior dismissal so the editor can
    // always see the current design. On the live site "Enabled" is ignored.
    if (isLivePreview()) {
      if (enabled !== false) setOpen(true);
      return;
    }

    let dismissed = false;
    try {
      dismissed = window.sessionStorage.getItem(storageKey) === "1";
    } catch {
      dismissed = false;
    }
    if (dismissed) return;

    const delayMs = Math.max(0, Number(delay) || 0) * 1000;
    const timer = window.setTimeout(() => setOpen(true), delayMs);
    return () => window.clearTimeout(timer);
  }, [delay, storageKey, enabled]);

  // Once the popup is visible, optionally redirect the window after the timeout.
  useEffect(() => {
    if (!open) return;
    // Never redirect while editing in live preview.
    if (isLivePreview()) return;

    const timeoutMs = Math.max(0, Number(redirectTimeout) || 0) * 1000;
    if (timeoutMs <= 0 || !redirectUrl) return;

    const timer = window.setTimeout(() => {
      window.location.href = redirectUrl;
    }, timeoutMs);
    return () => window.clearTimeout(timer);
  }, [open, redirectTimeout, redirectUrl]);

  // Lock body scroll, wire up Escape, and focus the dialog while open.
  useEffect(() => {
    if (!open) return;

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") close();
    };
    document.addEventListener("keydown", onKeyDown);

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    modalRef.current?.focus();

    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = previousOverflow;
    };
  }, [open, close]);

  // "Enabled" only gates the live-preview render; on the live site it is ignored.
  if (isLivePreview() && enabled === false) return null;
  if (!open) return null;

  return (
    <div className={styles.Scrim} onClick={close} role="presentation">
      <div
        ref={modalRef}
        className={styles.Modal}
        role="dialog"
        aria-modal="true"
        aria-labelledby="kv-popup-title"
        tabIndex={-1}
        onClick={onModalClick}
      >
        <button
          type="button"
          className={styles.Close}
          onClick={close}
          aria-label="Close"
        >
          <svg
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <path d="M6 6l12 12" />
            <path d="M18 6L6 18" />
          </svg>
        </button>
        <RichText content={content} />
      </div>
    </div>
  );
};

export default Popup;
